'use client'

import type { ComponentProps, ReactNode } from 'react'

import { ArrowRightIcon, CopyIcon } from '@/components/icons/icon-set'
import { cn } from '@/lib/utils'

/*
 * GoodUnited / LinkCard. Figma: 10 / Components / Table (LinkCard, TableCell link).
 * A compact tile on Colors/surface/subtle: 16px icon, label (optional small caption), trailing 24px outline action.
 * action navigate shows an arrow, action copy copies the value and shows the link in Colors/text/link.
 */
interface LinkCardProps extends Omit<ComponentProps<'div'>, 'children'> {
    icon: ReactNode
    label: string
    caption?: string
    action?: 'navigate' | 'copy'
    href?: string
    onAction?: () => void
}

function LinkCard({ icon, label, caption, action = 'navigate', href, onAction, className, ...props }: LinkCardProps) {
    const handleAction = () => {
        if (action === 'copy' && typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(href ?? label).catch(() => undefined)
        }
        onAction?.()
    }

    return (
        <div
            data-slot='link-card'
            className={cn('flex h-10 min-w-0 items-center gap-3 rounded-md bg-surface-subtle py-1 pr-1 pl-3 dark:bg-surface-muted', className)}
            {...props}
        >
            <span className='inline-flex shrink-0 text-icon-secondary [&_svg]:size-4'>
                {icon}
            </span>
            <span className='flex min-w-0 flex-1 flex-col leading-tight'>
                {caption && (
                    <span className='truncate text-xs text-text-secondary'>
                        {caption}
                    </span>
                )}
                <span className={cn('truncate text-sm font-medium', action === 'copy' ? 'text-text-link' : 'text-text-primary')}>
                    {label}
                </span>
            </span>
            <button
                type='button'
                onClick={handleAction}
                aria-label={action === 'copy' ? `Copy ${label}` : `Open ${label}`}
                className='inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-action-secondary-border bg-action-secondary text-icon-primary transition-colors outline-none hover:bg-action-secondary-hover focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-focus [&_svg]:size-4'
            >
                {action === 'copy' ? <CopyIcon aria-hidden='true' /> : <ArrowRightIcon aria-hidden='true' />}
            </button>
        </div>
    )
}

export { LinkCard }
